"use strict";
import { Text as TamaguiText, isWeb, styled } from "tamagui";
import { Flex } from "ui/src/components/layout";
import { HiddenFromScreenReaders } from "ui/src/components/text/HiddenFromScreenReaders";
import { useEnableFontScaling } from "ui/src/components/text/useEnableFontScaling";
import { Skeleton } from "ui/src/loading/Skeleton";
import { fonts } from "ui/src/theme/fonts";
export const TextFrame = styled(TamaguiText, {
  fontFamily: "$body",
  wordWrap: "break-word",
  variants: {
    variant: {
      heading1: {
        fontFamily: "$heading",
        fontSize: "$large",
        lineHeight: "$large",
        fontWeight: "$bold",
        maxFontSizeMultiplier: fonts.heading1.maxFontSizeMultiplier
      },
      heading2: {
        fontFamily: "$heading",
        fontSize: "$medium",
        lineHeight: "$medium",
        fontWeight: "$bold",
        maxFontSizeMultiplier: fonts.heading2.maxFontSizeMultiplier
      },
      heading3: {
        fontFamily: "$heading",
        fontSize: "$small",
        lineHeight: "$small",
        fontWeight: "$medium",
        maxFontSizeMultiplier: fonts.heading3.maxFontSizeMultiplier
      },
      subheading1: {
        fontFamily: "$subHeading",
        fontSize: "$large",
        lineHeight: "$large",
        fontWeight: "$medium",
        maxFontSizeMultiplier: fonts.subheading1.maxFontSizeMultiplier
      },
      subheading2: {
        fontFamily: "$subHeading",
        fontSize: "$small",
        lineHeight: "$small",
        fontWeight: "$medium",
        maxFontSizeMultiplier: fonts.subheading2.maxFontSizeMultiplier
      },
      body1: {
        fontFamily: "$body",
        fontSize: "$large",
        lineHeight: "$large",
        fontWeight: "$normal",
        maxFontSizeMultiplier: fonts.body1.maxFontSizeMultiplier,
        letterSpacing: "$tight",
        wordSpacing: "$tight"
      },
      body2: {
        fontFamily: "$body",
        fontSize: "$medium",
        lineHeight: "$medium",
        fontWeight: "$normal",
        maxFontSizeMultiplier: fonts.body2.maxFontSizeMultiplier,
        letterSpacing: "$tight",
        wordSpacing: "$tight"
      },
      body3: {
        fontFamily: "$body",
        fontSize: "$small",
        lineHeight: "$small",
        fontWeight: "$normal",
        maxFontSizeMultiplier: fonts.body3.maxFontSizeMultiplier,
        letterSpacing: "$tight",
        wordSpacing: "$tight"
      },
      body4: {
        fontFamily: "$body",
        fontSize: "$micro",
        lineHeight: "$micro",
        fontWeight: "$normal",
        maxFontSizeMultiplier: fonts.body4.maxFontSizeMultiplier,
        letterSpacing: "$tight",
        wordSpacing: "$tight"
      },
      buttonLabel1: {
        fontFamily: "$button",
        fontSize: "$large",
        lineHeight: "$large",
        fontWeight: "$medium",
        maxFontSizeMultiplier: fonts.buttonLabel1.maxFontSizeMultiplier
      },
      buttonLabel2: {
        fontFamily: "$button",
        fontSize: "$medium",
        lineHeight: "$medium",
        fontWeight: "$medium",
        maxFontSizeMultiplier: fonts.buttonLabel2.maxFontSizeMultiplier
      },
      buttonLabel3: {
        fontFamily: "$button",
        fontSize: "$small",
        lineHeight: "$small",
        fontWeight: "$medium",
        maxFontSizeMultiplier: fonts.buttonLabel3.maxFontSizeMultiplier
      },
      buttonLabel4: {
        fontFamily: "$button",
        fontSize: "$micro",
        lineHeight: "$micro",
        fontWeight: "$medium",
        maxFontSizeMultiplier: fonts.buttonLabel4.maxFontSizeMultiplier
      },
      monospace: {
        fontFamily: "$body",
        fontSize: fonts.body2.fontSize,
        lineHeight: fonts.body2.lineHeight,
        fontWeight: "$normal",
        maxFontSizeMultiplier: fonts.body2.maxFontSizeMultiplier
      }
    }
  },
  defaultVariants: {
    variant: "body2"
  }
});
const Heading1 = styled(TextFrame, {
  tag: "h1"
});
const Heading2 = styled(TextFrame, {
  tag: "h2"
});
const Heading3 = styled(TextFrame, {
  tag: "h3"
});
export const TextPlaceholder = ({ children }) => {
  return <Flex row alignItems="center" testID="text-placeholder">
      <Flex row alignItems="center" position="relative">
        <HiddenFromScreenReaders>{children}</HiddenFromScreenReaders>
        <Flex
    backgroundColor={isWeb ? "$surface3" : "$surface2"}
    borderRadius="$roundedFull"
    bottom="5%"
    left={0}
    position="absolute"
    right={0}
    top="5%"
  />
      </Flex>
    </Flex>;
};
export const TextLoaderWrapper = ({
  children,
  loadingShimmer
}) => {
  const inner = <TextPlaceholder>{children}</TextPlaceholder>;
  if (loadingShimmer) {
    return <Skeleton>{inner}</Skeleton>;
  }
  return inner;
};
const TEXT_COMPONENTS = {
  heading1: Heading1,
  heading2: Heading2,
  heading3: Heading3
};
const getTextComponent = (variant) => {
  return TEXT_COMPONENTS[variant] ?? TextFrame;
};
export const Text = TextFrame.styleable(
  ({ loading = false, allowFontScaling, loadingPlaceholderText = "000.00", ...rest }, ref) => {
    const enableFontScaling = useEnableFontScaling(allowFontScaling);
    const TextComponent = getTextComponent(rest.variant);
    if (loading) {
      return <TextLoaderWrapper loadingShimmer={loading !== "no-shimmer"}>
          <TextComponent ref={ref} allowFontScaling={enableFontScaling} color="$transparent" opacity={0} {...rest}>
            {
        /* Important that `children` isn't used or rendered by <Text> when `loading` is true, because if the child of a <Text> component is a dynamic variable that might not be finished fetching yet, it'll result in an error until it's finished loading. We use `loadingPlaceholderText` to set the size of the loading element instead. */
      }
            {loadingPlaceholderText}
          </TextComponent>
        </TextLoaderWrapper>;
    }
    return <TextComponent allowFontScaling={enableFontScaling} color="$neutral1" {...rest} />;
  }
);
